// progress-ui.js
import { clamp, qs, qsa, exKey, exRoundKey, blockKey, getTotalRounds } from './utils.js';

export default class ProgressUI {
  /** Creates a DOM renderer backed by a ProgressStore. @param {import('./progress-store.js').default} store */
  constructor(store) {
    this.store = store;
    this._pendingFocusTimeout = null;
    this._wiredWeek = null;
    this._wiredDay = null;
  }

  /** Applies minimum dimensions required for a visible progress bar. @param {HTMLElement} el @returns {void} */
  static ensureBarStyles(el) {
    const cs = getComputedStyle(el);
    const w = parseFloat(cs.width) || 0;
    const h = parseFloat(cs.height) || 0;
    if (w < 24) el.style.width = '72px';
    if (h < 4) el.style.height = '8px';
    // `block`, not `inline-block`. Every bar in the app is a standalone
    // element on its own line, never a bar inside a run of text, and as an
    // inline-block each one sat in a line box that added half-leading above
    // and descender space below -- space nothing declared and nothing wanted.
    // This runs on every bar, and an inline style beats the stylesheet, so
    // `inline-block` here silently overrode the `display: block` in
    // _progress-bars.scss: the gap under the day bar measured 20px against
    // the 8px it was given and the gap above the first block 58px against 48.
    el.style.display = 'block';
    el.style.borderRadius = '9999px';
  }

  /** Returns exercise identifiers represented in a block. @param {Element} blockEl @returns {string[]} */
  static eachBlockExerciseIds(blockEl) {
    return qsa('.exercise-row[data-ex]', blockEl).map((el) => el.getAttribute('data-ex'));
  }

  /** Reconciles the current round into legacy display state, then persists that ephemeral map. @param {number|string} week @param {number|string} day @param {Element} blockEl @returns {void} */
  repaintBlockCurrentRound(week, day, blockEl) {
    const blockId = blockEl.getAttribute('data-block-id');
    const totalRounds = getTotalRounds(blockEl);
    const curRound = this.store.getCurrentRound(week, day, blockId, totalRounds);
    const blockDone = curRound >= totalRounds;
    let completionChanged = false;

    qsa('.exercise-row[data-ex]', blockEl).forEach((row) => {
      const ex = row.getAttribute('data-ex');
      const simple = exKey(week, day, ex);
      // Canonical round history wins; legacy state remains a fallback only before history exists.
      const v =
        blockDone ||
        !!this.store.exRounds[exRoundKey(week, day, blockId, curRound, ex)] ||
        (!this.store.hasRoundHistoryForExercise(week, day, ex) && !!this.store.exCompleted[simple]);

      if (v && this.store.exCompleted[simple] !== true) {
        this.store.exCompleted[simple] = true;
        completionChanged = true;
      } else if (!v && Object.hasOwn(this.store.exCompleted, simple)) {
        delete this.store.exCompleted[simple];
        completionChanged = true;
      }

      row.classList.toggle('is-done', v);
      const btn = qs('.mark-ex[data-ex]', row);
      if (btn) {
        btn.classList.toggle('is-done', v);
        btn.setAttribute('aria-pressed', String(v));
      }
      row.querySelectorAll('.timer-start').forEach((b) => b.classList.toggle('is-done', v));
    });
    if (completionChanged) this.store.markExercisesDirty();
    this.store.persistExercises();
    this.markCurrentExercise();
  }

  /** Repaints a block from stored state without mutating or persisting completion maps. @param {number|string} week @param {number|string} day @param {Element} blockEl @returns {void} */
  renderBlockStoredState(week, day, blockEl) {
    const blockId = blockEl.getAttribute('data-block-id');
    const totalRounds = getTotalRounds(blockEl);
    const currentRound = this.store.getCurrentRound(week, day, blockId, totalRounds);
    const blockDone = currentRound >= totalRounds;
    this.updateBlockRoundsUI(blockEl, currentRound, totalRounds);
    qsa('.exercise-row[data-ex]', blockEl).forEach((row) => {
      const ex = row.getAttribute('data-ex');
      const completed =
        blockDone ||
        !!this.store.exRounds[exRoundKey(week, day, blockId, currentRound, ex)] ||
        (!this.store.hasRoundHistoryForExercise(week, day, ex) &&
          !!this.store.exCompleted[exKey(week, day, ex)]);
      row.classList.toggle('is-done', completed);
      const button = qs('.mark-ex[data-ex]', row);
      if (button) {
        button.classList.toggle('is-done', completed);
        button.setAttribute('aria-pressed', String(completed));
      }
      qsa('.timer-start', row).forEach((button) => button.classList.toggle('is-done', completed));
    });
    this.markCurrentExercise();
  }

  /** Tests whether each block exercise is complete in a legacy map. @param {number|string} week @param {number|string} day @param {Element} blockEl @param {Record<string, boolean>|null} [exCompletedMap=null] @returns {boolean} */
  static allExercisesDoneInBlock(week, day, blockEl, exCompletedMap) {
    const ids = ProgressUI.eachBlockExerciseIds(blockEl);
    if (ids.length === 0) return false;
    const state = exCompletedMap || null;
    if (state) return ids.every((ex) => !!state[exKey(week, day, ex)]);
    return false;
  }

  /** Tests whether each block exercise is complete in one zero-based canonical round. @param {number|string} week @param {number|string} day @param {Element} blockEl @param {number} roundIndex @param {Record<string, boolean>} exRounds @returns {boolean} */
  static allExercisesDoneInSpecificRound(week, day, blockEl, roundIndex, exRounds) {
    const ids = ProgressUI.eachBlockExerciseIds(blockEl);
    if (ids.length === 0) return false;
    const blockId = blockEl.getAttribute('data-block-id');
    return ids.every((ex) => {
      const k = exRoundKey(week, day, blockId, roundIndex, ex);
      return !!exRounds[k];
    });
  }

  /** Updates round UI from a completed-round count; the display itself is one-based. @param {Element} blockEl @param {number} current @param {number} total @returns {void} */
  updateBlockRoundsUI(blockEl, current, total) {
    const bar = qs('.block-rounds-bar', blockEl);
    const labelCur = qs('.block-rounds-label .current', blockEl);
    const pct = clamp(Math.round((current / total) * 100), 0, 100);
    if (labelCur) {
      const display = Math.min(current + 1, total);
      labelCur.textContent = String(display);
    }
    if (bar) {
      const blockLabel = qs('.exercise-block__title', blockEl)?.textContent.trim() || 'Block';
      ProgressUI.ensureBarStyles(bar);
      bar.setAttribute('role', 'progressbar');
      bar.setAttribute('aria-valuemin', '0');
      bar.setAttribute('aria-valuemax', String(total));
      bar.setAttribute('aria-valuenow', String(current));
      bar.setAttribute('aria-label', `${blockLabel}: ${current} of ${total} rounds complete`);
      bar.style.setProperty('--progress-pct', `${pct}%`);
    }
    const chips = qs('.round-chips[data-total-rounds]', blockEl);
    if (chips) {
      Array.from(chips.querySelectorAll('.chip')).forEach((el, idx) => {
        el.classList.toggle('is-done', idx < current);
      });
    }
    const btn = qs('.finish-round', blockEl);
    if (btn) btn.disabled = current >= total;
    blockEl.classList.toggle('is-done', current >= total);
  }

  /** Resets non-running peer timers without interrupting the timer that completed the source event. @param {Element} blockEl @param {import('./timer.js').default|null} [sourceTimer=null] @returns {void} */
  resetTimersInBlock(blockEl, sourceTimer = null) {
    // Timer completion and the resulting round repaint are synchronous. Keep
    // that source timer stable until it has emitted done and closed takeover.
    qsa('.ex-timer', blockEl).forEach((el) => {
      if (el.__timer && el.__timer !== sourceTimer && !el.__timer.running) el.__timer.reset();
    });
  }

  /** Returns the scroll position that leaves an element below the sticky header. @param {Element} el @returns {number} */
  getScrollTop(el) {
    const rs = getComputedStyle(document.documentElement);
    const varVal = rs.getPropertyValue('--header-height');
    let offset = 0;
    if (varVal) {
      const m = String(varVal).match(/([0-9.]+)/);
      if (m) offset = parseFloat(m[1]);
    }
    if (!offset) {
      const header = qs('header');
      if (header?.offsetHeight) offset = header.offsetHeight;
    }
    if (!offset) offset = 64;
    const rect = el.getBoundingClientRect();
    return Math.max(0, rect.top + window.scrollY - offset - 8);
  }

  /** Clears the one deferred focus operation owned by this UI instance. @returns {void} */
  cancelPendingFocus() {
    if (this._pendingFocusTimeout !== null) clearTimeout(this._pendingFocusTimeout);
    this._pendingFocusTimeout = null;
  }

  /** Returns whether the user has asked the browser to reduce motion. @returns {boolean} */
  prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /**
   * @private Pops the completion tick on one button.
   *
   * Called only from `completeExercise`, on a false-to-true transition. The
   * repaint methods set `.is-done` too -- from storage on load, and across a
   * whole block when a round advances -- and firing from there would replay
   * the animation for exercises the user finished minutes ago.
   * @param {HTMLElement|null} button
   * @returns {void}
   */
  _popCompletionTick(button) {
    if (!button || this.prefersReducedMotion()) return;
    button.classList.remove('is-just-done');
    // Reading a layout property flushes the removal, so completing a second
    // exercise in the same frame restarts the animation rather than being
    // dropped as a no-op class toggle.
    void button.offsetWidth;
    button.classList.add('is-just-done');
    button.addEventListener('animationend', () => button.classList.remove('is-just-done'), {
      once: true,
    });
  }

  /** @private Scrolls below the sticky header and optionally restores a local focus target. */
  _scrollToElement(el, focusSelector = null) {
    try {
      const focusTarget = focusSelector ? qs(focusSelector, el) : null;
      if (focusSelector) this.cancelPendingFocus();
      if (focusSelector && !focusTarget) return;
      const reduced = this.prefersReducedMotion();
      window.scrollTo({ top: this.getScrollTop(el), behavior: reduced ? 'auto' : 'smooth' });
      if (focusSelector) {
        if (reduced) focusTarget.focus({ preventScroll: true });
        else {
          this._pendingFocusTimeout = setTimeout(() => {
            this._pendingFocusTimeout = null;
            focusTarget.focus({ preventScroll: true });
          }, 250);
        }
      }
    } catch (e) {
      console.warn('Scroll failed', e);
    }
  }

  /** Scrolls a block into view and focuses its first completion control. @param {Element} blockEl @returns {void} */
  scrollBlockIntoView(blockEl) {
    this._scrollToElement(blockEl, '.mark-ex');
  }

  /** Scrolls to the first visible unfinished exercise. @returns {void} */
  scrollToNextUndoneExercise() {
    const nextEx = qs('.exercise-row:not(.is-done)');
    if (nextEx) {
      this._scrollToElement(nextEx, '.mark-ex');
    }
  }

  /**
   * Marks the first not-done exercise as the current one, always -- unlike
   * `.is-active`, which only appears while a timer is running (and is
   * covered by that timer's own fullscreen takeover while it does) and never
   * appears at all for a timerless exercise. This is the one that stays
   * visible: same "first not-done row" definition `scrollToNextUndoneExercise`
   * already uses, so the card the page scrolls to is the card marked current.
   *
   * Called from the end of every method that settles `.is-done` on rows --
   * `repaintBlockCurrentRound`, `renderBlockStoredState`, `completeExercise`
   * -- rather than from `updateDayProgressBars`, which several of those call
   * *before* touching row state, and would otherwise read it stale.
   * @returns {void}
   */
  markCurrentExercise() {
    const current = qs('.exercise-row:not(.is-done)');
    qsa('.exercise-card.is-current').forEach((card) => {
      if (card !== current) {
        card.classList.remove('is-current');
        card.removeAttribute('aria-current');
      }
    });
    if (current) {
      current.classList.add('is-current');
      current.setAttribute('aria-current', 'step');
    }
  }

  /** Updates all bars for one day using round-aware completion count divided by template total. @param {number|string} week @param {number|string} day @returns {void} */
  updateDayProgressBars(week, day) {
    const done = this.store.getExDoneForDay(week, day);
    let dayTotal = 0;

    qsa(`.progress-bar[data-week="${week}"][data-day="${day}"]`).forEach((pb) => {
      const total = parseInt(pb.getAttribute('data-ex-total') || '0', 10);
      if (total > dayTotal) dayTotal = total;
      const p = total > 0 ? (done / total) * 100 : 0;

      pb.style.setProperty('--progress-pct', `${p}%`);
      if (p >= 100) pb.classList.add('is-done');
      else pb.classList.remove('is-done');

      pb.setAttribute('role', 'progressbar');
      pb.setAttribute('aria-valuemin', '0');
      pb.setAttribute('aria-valuemax', '100');
      pb.setAttribute('aria-valuenow', String(Math.round(p)));
      pb.setAttribute(
        'aria-label',
        total > 0
          ? `Week ${week}, Day ${day}: completed ${done} of ${total} exercises`
          : `Week ${week}, Day ${day}: completed 0 of 0 exercises`
      );
      ProgressUI.ensureBarStyles(pb);
      if (pb.textContent) pb.textContent = '';
    });

    this.syncDayCardState(week, day, done, dayTotal);
    this.syncDayCompletion(week, day, done, dayTotal);
  }

  /**
   * Corrects the home screen's resume card to the stored position.
   *
   * Opening the app put you at Week 1 Day 1 of a programme you may be six
   * weeks into, so every session began with scrolling and remembering. The
   * card answers that before the catalogue starts, and the catalogue stays
   * underneath because looking ahead is genuinely useful -- it just stops
   * being the first thing you have to navigate past.
   *
   * The card is server-rendered in its fresh-visit state and this rewrites it
   * in place, so nothing is inserted after first paint and the catalogue below
   * never moves. If there are no day cards on the page there is nothing to
   * resolve, and the markup's own copy stands rather than being guessed at.
   *
   * @returns {void}
   */
  renderResumeCard() {
    const card = qs('#resume-card');
    if (!card) return;

    const days = qsa('.day-card .progress-bar[data-week][data-day]').map((pb) => ({
      week: pb.dataset.week,
      day: pb.dataset.day,
      total: parseInt(pb.getAttribute('data-ex-total') || '0', 10),
    }));

    const target = this.store.resume(days);
    if (!target) return;

    const { week, day, total, done, state } = target;
    const link = qs(`.day-card__link[href="/week/${week}/day/${day}/"]`);
    // The day cards already say `Day 2: EMOM 24`; only the title half is new
    // information here, because the card states the day in its own line.
    const title = (link?.textContent || '').replace(/^Day\s+\d+:\s*/i, '').trim();
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;

    const copy = {
      'in-progress': { eyebrow: 'Continue', action: 'Resume', count: `${done} of ${total} done` },
      'not-started': { eyebrow: 'Up next', action: 'Start', count: `${total} exercises` },
      finished: { eyebrow: 'Programme complete', action: 'Review', count: 'Every day done' },
    }[state];

    const set = (name, text) => {
      const el = qs(`[data-resume="${name}"]`, card);
      if (el) el.textContent = text;
    };
    set('eyebrow', copy.eyebrow);
    // An en dash, not a middot: Axiforma draws `·` on the baseline, so it read
    // as a full stop in the middle of the line.
    set('where', `Week ${week} – Day ${day}`);
    set('title', title);
    set('count', copy.count);

    const bar = qs('[data-resume="bar"]', card);
    if (bar) {
      bar.style.setProperty('--progress-pct', `${pct}%`);
      bar.classList.toggle('is-done', pct >= 100);
      bar.setAttribute('aria-valuenow', String(pct));
      bar.setAttribute(
        'aria-label',
        `Week ${week}, Day ${day}: completed ${done} of ${total} exercises`
      );
      ProgressUI.ensureBarStyles(bar);
    }

    const action = qs('[data-resume="action"]', card);
    if (action) {
      action.setAttribute('href', `/week/${week}/day/${day}/`);
      action.textContent = copy.action;
      // `Start` alone is the whole link text, and out of the card's visual
      // context it says nothing about where it goes -- a screen reader running
      // a links list gets one unlabelled verb. The visible word stays first so
      // the accessible name still contains the label (WCAG 2.5.3).
      action.setAttribute('aria-label', `${copy.action} Week ${week}, Day ${day}`);
    }

    card.hidden = false;

    // The card is in the markup from the start, so on a first visit this call
    // rewrites it with exactly what is already on screen -- and replacing the
    // text of a `role="status"` announces it, whether or not it changed. Only
    // an actual correction is worth a screen reader's attention, so the line
    // is compared before it is written. One line, not the whole card read back.
    const announce = `${copy.eyebrow}: Week ${week}, Day ${day}. ${copy.count}.`;
    const announceEl = qs('[data-resume="announce"]', card);
    if (announceEl && announceEl.textContent.trim() !== announce) {
      announceEl.textContent = announce;
    }
  }

  /**
   * Marks a day card as untouched, started or finished.
   *
   * A list of eight identical cards gives no answer to "where was I?", and the
   * progress bar alone is a thin line that reads as decoration until you know
   * to look for it. The state goes on the card so the whole card carries it,
   * and the count says it in words for anyone who cannot see the bar.
   *
   * `is-complete`, not `is-done`: `.day-card.is-done` already means the faded,
   * muted treatment shared with exercise cards, and a finished day should be
   * the most legible thing in the list, not the least.
   *
   * @param {number|string} week
   * @param {number|string} day
   * @param {number} done
   * @param {number} total
   * @returns {void}
   */
  syncDayCardState(week, day, done, total) {
    const started = total > 0 && done > 0 && done < total;
    const complete = total > 0 && done >= total;

    qsa(`.day-card__count[data-week="${week}"][data-day="${day}"]`).forEach((count) => {
      const card = count.closest('.day-card');
      if (card) {
        card.classList.toggle('is-started', started);
        card.classList.toggle('is-complete', complete);
      }

      if (complete) count.textContent = 'Done';
      else if (started) count.textContent = `${done} of ${total}`;
      else count.textContent = `${total} exercises`;
    });
  }

  /**
   * Reveals or hides this day's completion panel, and announces and scrolls to
   * it the moment it first appears.
   *
   * Finishing a day used to produce nothing at all: the bar reached 100% and
   * the page below it stayed empty. The panel is the acknowledgement and the
   * route onward, so it is driven from the one place that already knows the
   * day's count -- un-marking an exercise takes it away again, because a day
   * that is no longer complete should not still be claiming it is.
   *
   * The first call for a page only sets visibility. A day loaded already
   * complete should show its panel, but scrolling on load would fight the
   * browser's own scroll restoration and move the page under the user before
   * they have done anything.
   *
   * @param {number|string} week
   * @param {number|string} day
   * @param {number} done
   * @param {number} total
   * @returns {void}
   */
  syncDayCompletion(week, day, done, total) {
    const panel = qs(`#day-complete[data-week="${week}"][data-day="${day}"]`);
    if (!panel) return;

    const firstSync = !this._dayCompletionSynced;
    this._dayCompletionSynced = true;

    const complete = total > 0 && done >= total;
    if (complete === !panel.hidden) return;
    panel.hidden = !complete;
    if (!complete || firstSync) return;

    const sr = qs('#sr-updates');
    if (sr) sr.textContent = `Day complete: all ${total} exercises done`;
    this._scrollToElement(panel);
  }

  /** Wires an idempotent timer-update listener that marks the active exercise card. @returns {void} */
  wireTimerEvents() {
    if (this._timerUpdateHandler) return;
    this._timerUpdateHandler = (e) => {
      this.cancelPendingFocus();
      const timerEl = e.target;
      if (!(timerEl instanceof Element)) return;
      const card = timerEl.closest('.exercise-card');
      if (!card) return;

      const phase = e.detail?.phase;
      if (phase === 'active' || phase === 'prepare') {
        qsa('.exercise-card.is-active').forEach((c) => {
          if (c !== card) c.classList.remove('is-active');
        });
        card.classList.add('is-active');
      } else if (phase === 'done' || phase === 'idle') {
        card.classList.remove('is-active');
      }
    };
    this._timerCompletionHandler = (e) => {
      const timerEl = e.target;
      if (!(timerEl instanceof Element)) return;
      const row = timerEl.closest('.exercise-row');
      const button = row && qs('.mark-ex[data-ex]', row);
      if (!button || this._wiredWeek === null || this._wiredDay === null) return;
      this.completeExercise({
        week: this._wiredWeek,
        day: this._wiredDay,
        button,
        source: 'timer',
        sourceTimer: timerEl.__timer || null,
      });
    };
    document.addEventListener('timer:update', this._timerUpdateHandler);
    document.addEventListener('timer:complete-exercise', this._timerCompletionHandler);
  }

  /** Detaches delegated timer listeners and pending focus work. @returns {void} */
  destroy() {
    this.cancelPendingFocus();
    if (this._timerUpdateHandler)
      document.removeEventListener('timer:update', this._timerUpdateHandler);
    if (this._timerCompletionHandler)
      document.removeEventListener('timer:complete-exercise', this._timerCompletionHandler);
    this._exerciseHandlers?.forEach(({ el, type, handler }) =>
      el.removeEventListener(type, handler)
    );
    this._exerciseHandlers = null;
    this._timerUpdateHandler = null;
    this._timerCompletionHandler = null;
  }

  /** Wires exercise controls for a day, including timer-restart completion revocation. @param {number|string} week @param {number|string} day @returns {void} */
  wireExerciseButtons(week, day) {
    this._wiredWeek = week;
    this._wiredDay = day;
    this.wireTimerEvents();
    this._exerciseHandlers ||= [];
    qsa('.mark-ex[data-ex]').forEach((btn) => {
      const ex = btn.dataset.ex;
      const k = exKey(week, day, ex);
      const row = btn.closest('.exercise-row');
      const setExState = () => {
        const on = !!this.store.exCompleted[k];
        btn.classList.toggle('is-done', on);
        row?.classList.toggle('is-done', on);
        row?.querySelectorAll('.timer-start').forEach((b) => b.classList.toggle('is-done', on));
        btn.setAttribute('aria-pressed', String(on));
      };
      setExState();
      if (this._exerciseHandlers.some(({ el }) => el === btn)) return;
      const completeHandler = () => this.completeExercise({ week, day, button: btn });
      btn.addEventListener('click', completeHandler);
      this._exerciseHandlers.push({ el: btn, type: 'click', handler: completeHandler });

      // Only a fresh timer run revokes completion; pausing must preserve earned progress.
      row?.querySelectorAll('.ex-timer').forEach((timerEl) => {
        const restartHandler = () => {
          if (!this.store.exCompleted[k]) return;
          this.store.setExercise(week, day, ex, false);

          const blockEl = row?.closest('[data-block-id]') || null;
          if (blockEl) {
            const totalRounds = getTotalRounds(blockEl);
            const bid = blockEl.getAttribute('data-block-id');
            const curRound = this.store.getCurrentRound(week, day, bid, totalRounds);
            this.store.setExerciseRound(week, day, bid, curRound, ex, false);
          }
          this.updateDayProgressBars(week, day);
          setExState();
        };
        timerEl.addEventListener('timer:restart', restartHandler);
        this._exerciseHandlers.push({
          el: timerEl,
          type: 'timer:restart',
          handler: restartHandler,
        });
      });
    });
  }

  /** Completes or toggles an exercise, updates its round, then repaints and advances its block. @param {{week: number|string, day: number|string, button: HTMLButtonElement, source?: 'user'|'timer', sourceTimer?: import('./timer.js').default|null}} options @returns {boolean} */
  completeExercise({ week, day, button, source = 'user', sourceTimer = null }) {
    const ex = button.dataset.ex;
    if (!ex) return false;
    const row = button.closest('.exercise-row');
    const k = exKey(week, day, ex);
    const wasDone = !!this.store.exCompleted[k];
    const on = source === 'timer' ? true : !wasDone;
    if (source === 'timer' && wasDone) return false;

    this.store.setExercise(week, day, ex, on);
    if (source === 'user' && typeof navigator !== 'undefined' && navigator.vibrate)
      navigator.vibrate(12);

    let blockEl = row?.closest('[data-block-id]') || button.closest('[data-block-id]') || null;
    const blockId =
      blockEl?.getAttribute('data-block-id') || button.dataset.blockId || row?.dataset.blockId;
    if (!blockEl && blockId) blockEl = qs(`[data-block-id="${blockId}"]`);
    if (blockEl) {
      const totalRounds = getTotalRounds(blockEl);
      const bid = blockId || blockEl.getAttribute('data-block-id');
      const curRound = this.store.getCurrentRound(week, day, bid, totalRounds);
      this.store.setExerciseRound(week, day, bid, curRound, ex, on);
    }

    this.updateDayProgressBars(week, day);
    const advanced = blockEl
      ? this.advanceBlockRoundIfComplete(week, day, blockEl, null, null, {
          source,
          invoker: button,
          sourceTimer,
        })
      : false;
    const visible = !!this.store.exCompleted[k];
    button.classList.toggle('is-done', visible);
    button.setAttribute('aria-pressed', String(visible));
    if (visible && !wasDone) this._popCompletionTick(button);
    row?.classList.toggle('is-done', visible);
    row
      ?.querySelectorAll('.timer-start')
      .forEach((timerButton) => timerButton.classList.toggle('is-done', visible));
    this.markCurrentExercise();
    return advanced;
  }

  /**
   * Advances through consecutive fully complete zero-based rounds, repainting each iteration.
   * @param {number|string} week
   * @param {number|string} day
   * @param {Element} blockEl
   * @param {Record<string, number>|null} [blockRoundsMap=null]
   * @param {Record<string, boolean>|null} [exCompletedMap=null] Retained for compatibility and unused.
   * @param {{sideEffects?: boolean, source?: 'user'|'timer', invoker?: HTMLElement|null, sourceTimer?: import('./timer.js').default|null}} [options]
   * @returns {boolean}
   */
  advanceBlockRoundIfComplete(
    week,
    day,
    blockEl,
    blockRoundsMap,
    exCompletedMap,
    { sideEffects = true, source = 'user', invoker = null, sourceTimer = null } = {}
  ) {
    const blockId = blockEl.getAttribute('data-block-id');
    const totalRounds = getTotalRounds(blockEl);
    const bKey = blockKey(week, day, blockId);
    const blockRounds = blockRoundsMap || this.store.blockRounds;
    let cur = clamp(blockRounds[bKey] || 0, 0, totalRounds);
    let advanced = 0;

    // Catch up every sequential canonical round; legacy state is intentionally ignored here.
    while (cur < totalRounds) {
      if (
        ProgressUI.allExercisesDoneInSpecificRound(week, day, blockEl, cur, this.store.exRounds)
      ) {
        cur++;
        blockRounds[bKey] = cur;
        if (blockRounds === this.store.blockRounds) this.store.markBlockRoundsDirty();
        advanced++;

        // Each pass repaints and reconciles exercise state for the newly current round.
        this.repaintBlockCurrentRound(week, day, blockEl);
      } else {
        break;
      }
    }

    // Consolidate block-round persistence after all catch-up iterations complete.
    if (blockRounds === this.store.blockRounds) this.store.persistBlockRounds();

    // When advancement occurred, update summary UI and announce/reset/scroll only once.
    if (advanced > 0) {
      this.updateBlockRoundsUI(blockEl, cur, totalRounds);
      this.updateDayProgressBars(week, day);

      if (!sideEffects) return true;
      const heading = qs('h1, h2, h3, h4, h5, h6', blockEl)?.textContent?.trim() || 'Block';
      const sr = qs('#sr-updates');
      if (cur >= totalRounds) {
        this.cancelPendingFocus();
        // The last block of a day finishes the day, and both want the same
        // live region. `updateDayProgressBars` above has already written the
        // day-complete announcement, which outranks this one -- overwriting it
        // would leave the user told a block ended and never told the workout
        // did. On a page with no panel, or a day still in progress, this is the
        // only message there is.
        const dayPanel = qs('#day-complete');
        const dayComplete = !!dayPanel && !dayPanel.hidden;
        if (sr && !dayComplete)
          sr.textContent = `${heading} complete: ${cur} of ${totalRounds} rounds`;
      } else {
        if (sr) sr.textContent = `${heading}: round ${cur + 1} of ${totalRounds}`;
        this.resetTimersInBlock(blockEl, sourceTimer);
        if (source === 'user' && invoker) this.scrollBlockIntoView(blockEl);
      }

      return true;
    }
    return false;
  }
}
